import React from 'react';
import { IUserSchemaExtended, userSchema, UserService } from '@/api/user';
import { InputField } from '@/components/form/fields/InputField';
import {
  ISelectOption,
  SelectField,
} from '@/components/form/fields/SelectField';
import { UploadField } from '@/components/form/fields/UploadField';
import { FormContainer, FormFooter } from '@/components/form/FormContainer';
import { Section } from '@/components/Section';
import { Button, Dialog } from 'alurkerja-ui';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { AccessType, StatusUser } from '@/utils/constants/users';
import { Loading } from '@/components/Loading';
import { OutlineButton } from '@/components/shared/button/OutlineButton';

export const CreateEditUser: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isFetching: isFetchLoading } = UserService.useGet(id);
  const { mutate: create, isPending: isCreateLoading } =
    UserService.useCreate();
  const { mutate: update, isPending: isUpdateLoading } =
    UserService.useUpdate();
  const isLoading = isCreateLoading || isUpdateLoading;

  const form = useForm<IUserSchemaExtended>({
    resolver: zodResolver(userSchema),
    values: data,
  });

  const { isDirty } = form.formState;

  const statusUserOptions: ISelectOption[] = StatusUser.toOptions();
  const accessTypeOptions: ISelectOption[] = AccessType.toOptions();

  const submitAction = (data: IUserSchemaExtended) => {
    if (id) {
      update(data, {
        onSuccess: () => {
          Dialog.success({
            title: 'Berhasil',
            description: 'Berhasil update User',
            callback: () => navigate('/user', { replace: true }),
          });
        },
      });
    } else {
      create(data, {
        onSuccess: () => {
          Dialog.success({
            title: 'Berhasil',
            description: 'Berhasil membuat User',
            callback: () => navigate('/user', { replace: true }),
          });
        },
      });
    }
  };

  return (
    <Section title={id ? 'Update User' : 'Create User'}>
      {isFetchLoading ? (
        <Loading />
      ) : (
        <FormContainer form={form} onSubmit={submitAction}>
          <div className="flex flex-row gap-10">
            <div className="flex flex-col w-full gap-3">
              <InputField
                name="first_name"
                label="First Name"
                hasRequiredAsterisk
                disabled={id ? true : false}
              />

              <InputField
                name="last_name"
                label="Last Name"
                hasRequiredAsterisk
                disabled={id ? true : false}
              />

              <InputField name="email" label="Email" hasRequiredAsterisk disabled={id ? true : false}/>

              <SelectField
                name="status_user"
                label="Status User"
                hasRequiredAsterisk
                options={statusUserOptions}
              />
            </div>

            <div className="flex flex-col w-full gap-3 ">
              <SelectField
                name="user_type"
                label="User Type"
                hasRequiredAsterisk
                options={accessTypeOptions}
              />

              <div className="flex flex-col gap-2 p-3 bg-white rounded shadow-lg">
                <span>
                  <strong>Tester</strong> - Users with access type Tester will
                  have access to Dashboard and Projects Assigned to them
                  (Overview, Requirements, Attachments, Releases, Modules, Test
                  Cases, Test Plans, Test Runs, Issues, Jira Issues, Reports,
                  Project Integration Mapping)
                </span>

                <span>
                  <strong>Developer</strong> - Users with access type Developer
                  will access only to issues of Projects Assigned to them
                </span>
              </div>

              <UploadField
                name="medias"
                label="Photo"
                note="Allowed Formats: png, jpg, jpeg"
                type="image"
                disabled={id ? true : false}
              />
            </div>
          </div>

          <FormFooter>
            <OutlineButton onClick={() => navigate('/user', { replace: true })}>
              Back
            </OutlineButton>

            <Button loading={isLoading} disabled={!isDirty || isLoading}>
              {id ? 'Update' : 'Create'}
            </Button>
          </FormFooter>
        </FormContainer>
      )}
    </Section>
  );
};
